import { apiRequest } from '../config/api';

export interface Pedido {
  id: string;
  codigo: string;
  solicitacao_id?: string;
  fornecedor?: string;
  data_desejada?: string;
  peso?: number;
  volume?: number;
  status: 'Aguardando Pedido' | 'Pedido digitado' | 'Em preparação' | 'Em transporte' | 'Entregue' | 'Cancelado';
  numero_pedido?: string;
  arquivo_id?: string;
  documentos_digitados?: any[];
  prioridade: number;
  solicitante_id?: string;
  digitado_por?: string;
  data_digitacao?: string;
  observacoes?: string;
  filial: string;
  created_at: string;
  updated_at: string;
}

export interface DocumentoDigitado {
  nome: string;
  tipo: string;
  databaseKey: string;
  fornecedorMercadoria?: string;
  numeroPedido?: string;
  url?: string;
  tamanho?: number;
  uploadedAt?: string;
  uploadedBy?: string;
}

export interface CreatePedidoData {
  solicitacao_id?: string;
  fornecedor?: string;
  data_desejada?: string;
  peso?: number;
  volume?: number;
  status?: Pedido['status'];
  solicitante_id?: string;
  observacoes?: string;
  filial?: string;
}

export interface UpdatePedidoData {
  fornecedor?: string;
  data_desejada?: string;
  peso?: number;
  volume?: number;
  status?: Pedido['status'];
  numero_pedido?: string;
  arquivo_id?: string;
  documentos_digitados?: DocumentoDigitado[];
  digitado_por?: string;
  data_digitacao?: string;
  observacoes?: string;
}

// Dados mock para testes e validação
const mockPedidos: Pedido[] = [
  {
    id: 'mock-1',
    codigo: 'PED001',
    solicitacao_id: 'mock-sol-1',
    fornecedor: 'Fornecedor Teste A',
    data_desejada: '2024-04-20',
    peso: 1500.50,
    volume: 10.5,
    status: 'Aguardando Pedido',
    numero_pedido: undefined,
    arquivo_id: undefined,
    documentos_digitados: [],
    prioridade: 3,
    solicitante_id: 'user-1',
    digitado_por: undefined,
    data_digitacao: undefined,
    observacoes: 'Pedido de teste mock',
    filial: 'FILIAL_1',
    created_at: '2024-04-18T10:00:00Z',
    updated_at: '2024-04-18T10:00:00Z'
  },
  {
    id: 'mock-2',
    codigo: 'PED002',
    solicitacao_id: 'mock-sol-2',
    fornecedor: 'Fornecedor Teste B',
    data_desejada: '2024-04-19',
    peso: 2300.75,
    volume: 15.2,
    status: 'Aguardando Pedido',
    numero_pedido: undefined,
    arquivo_id: undefined,
    documentos_digitados: [],
    prioridade: 4,
    solicitante_id: 'user-2',
    digitado_por: undefined,
    data_digitacao: undefined,
    observacoes: 'Pedido urgente mock',
    filial: 'FILIAL_1',
    created_at: '2024-04-18T11:00:00Z',
    updated_at: '2024-04-18T11:00:00Z'
  },
  {
    id: 'mock-3',
    codigo: 'PED003',
    solicitacao_id: 'mock-sol-3',
    fornecedor: 'Fornecedor Teste C',
    data_desejada: '2024-04-25',
    peso: 1800.00,
    volume: 12.0,
    status: 'Pedido digitado',
    numero_pedido: 'NUM-2024-001',
    arquivo_id: 'file-mock-1',
    documentos_digitados: [
      {
        nome: 'documento1.pdf',
        tipo: 'application/pdf',
        databaseKey: 'doc1-key',
        fornecedorMercadoria: 'Produto A',
        numeroPedido: 'NUM-2024-001'
      }
    ],
    prioridade: 2,
    solicitante_id: 'user-3',
    digitado_por: 'user-admin',
    data_digitacao: '2024-04-18T14:30:00Z',
    observacoes: 'Pedido já digitado mock',
    filial: 'FILIAL_1',
    created_at: '2024-04-18T09:00:00Z',
    updated_at: '2024-04-18T14:30:00Z'
  },
  {
    id: 'mock-4',
    codigo: 'PED004',
    solicitacao_id: 'mock-sol-4',
    fornecedor: 'Fornecedor Teste D',
    data_desejada: '2024-04-30',
    peso: 3000.00,
    volume: 20.0,
    status: 'Aguardando Pedido',
    numero_pedido: undefined,
    arquivo_id: undefined,
    documentos_digitados: [],
    prioridade: 1,
    solicitante_id: 'user-4',
    digitado_por: undefined,
    data_digitacao: undefined,
    observacoes: 'Pedido de teste mock 4',
    filial: 'FILIAL_1',
    created_at: '2024-04-18T08:00:00Z',
    updated_at: '2024-04-18T08:00:00Z'
  },
  {
    id: 'mock-5',
    codigo: 'PED005',
    solicitacao_id: 'mock-sol-5',
    fornecedor: 'Fornecedor Teste E',
    data_desejada: '2024-04-18',
    peso: 1200.00,
    volume: 8.5,
    status: 'Aguardando Pedido',
    numero_pedido: undefined,
    arquivo_id: undefined,
    documentos_digitados: [],
    prioridade: 5, // Hoje - prioridade máxima
    solicitante_id: 'user-5',
    digitado_por: undefined,
    data_digitacao: undefined,
    observacoes: 'Pedido urgente mock',
    filial: 'FILIAL_1',
    created_at: '2024-04-18T07:00:00Z',
    updated_at: '2024-04-18T07:00:00Z'
  }
];

export class PedidosService {
  private static instance: PedidosService;

  static getInstance(): PedidosService {
    if (!PedidosService.instance) {
      PedidosService.instance = new PedidosService();
    }
    return PedidosService.instance;
  }

  // Obter dados mock
  static getMockData(): Pedido[] {
    return mockPedidos;
  }

  // Buscar todos os pedidos
  async buscarPedidos(filtros?: {
    status?: Pedido['status'][];
    fornecedor?: string;
    solicitacao_id?: string;
    data_inicio?: string;
    data_fim?: string;
    filial?: string;
    solicitante_id?: string;
  }): Promise<{ data: Pedido[] | null; error: any }> {
    try {
      const params = new URLSearchParams();
      if (filtros?.filial) params.append('filial', filtros.filial);
      if (filtros?.fornecedor) params.append('fornecedor', filtros.fornecedor);
      if (filtros?.solicitacao_id) params.append('solicitacao_id', filtros.solicitacao_id);
      if (filtros?.solicitante_id) params.append('solicitante_id', filtros.solicitante_id);
      if (filtros?.status && filtros.status.length > 0) params.append('status', filtros.status.join(','));

      const result = await apiRequest(`/pedidos?${params.toString()}`);
      return { data: result.data || [], error: null };
    } catch (error) {
      console.error('Erro ao buscar pedidos:', error);
      return { data: null, error };
    }
  }

  // Buscar pedidos pendentes (para digitação)
  async buscarPedidosPendentes(filial?: string): Promise<{ data: Pedido[] | null; error: any }> {
    try {
      const params = new URLSearchParams();
      if (filial) params.append('filial', filial);

      const result = await apiRequest(`/pedidos/pendentes?${params.toString()}`);
      return { data: result.data || [], error: null };
    } catch (error) {
      console.error('Erro ao buscar pedidos pendentes:', error);
      return { data: null, error };
    }
  }

  // Buscar pedidos do usuário
  async buscarPedidosUsuario(usuarioId: string, filial?: string): Promise<{ data: Pedido[] | null; error: any }> {
    try {
      const params = new URLSearchParams({ solicitante_id: usuarioId });
      if (filial) params.append('filial', filial);

      const result = await apiRequest(`/pedidos?${params.toString()}`);
      return { data: result.data || [], error: null };
    } catch (error) {
      console.error('Erro ao buscar pedidos do usuário:', error);
      return { data: null, error };
    }
  }

  // Buscar pedido por ID
  async buscarPedidoPorId(id: string): Promise<{ data: Pedido | null; error: any }> {
    try {
      const result = await apiRequest(`/pedidos/${id}`);
      return { data: result.data, error: null };
    } catch (error) {
      console.error('Erro ao buscar pedido:', error);
      return { data: null, error };
    }
  }

  // Criar pedido
  async criarPedido(pedidoData: CreatePedidoData): Promise<{ data: Pedido | null; error: any }> {
    try {
      const result = await apiRequest('/pedidos', {
        method: 'POST',
        body: JSON.stringify(pedidoData)
      });
      return { data: result.data, error: null };
    } catch (error) {
      console.error('Erro ao criar pedido:', error);
      return { data: null, error };
    }
  }

  // Criar pedido a partir de solicitação
  async criarPedidoDeSolicitacao(solicitacaoId: string, solicitanteId?: string): Promise<{ data: Pedido | null; error: any }> {
    try {
      const result = await apiRequest(`/pedidos/de-solicitacao/${solicitacaoId}`, {
        method: 'POST',
        body: JSON.stringify({ solicitante_id: solicitanteId })
      });
      return { data: result.data, error: null };
    } catch (error) {
      console.error('Erro ao criar pedido a partir de solicitação:', error);
      return { data: null, error };
    }
  }

  // Atualizar pedido
  async atualizarPedido(id: string, pedidoData: UpdatePedidoData): Promise<{ data: Pedido | null; error: any }> {
    try {
      const result = await apiRequest(`/pedidos/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(pedidoData)
      });
      return { data: result.data, error: null };
    } catch (error) {
      console.error('Erro ao atualizar pedido:', error);
      return { data: null, error };
    }
  }

  // Verificar se número de pedido já existe
  async verificarNumeroPedido(numeroPedido: string): Promise<{ existe: boolean; error: any }> {
    try {
      const result = await apiRequest(`/pedidos/verificar-numero?numero=${encodeURIComponent(numeroPedido)}`);
      return { existe: result.existe, error: null };
    } catch (error) {
      console.error('Erro ao verificar número do pedido:', error);
      return { existe: false, error };
    }
  }

  // Confirmar pedido (digitar número do pedido e upload de arquivo)
  async confirmarPedido(
    pedidoId: string,
    numeroPedido: string,
    arquivoId?: string,
    usuarioId?: string
  ): Promise<{ data: Pedido | null; error: any }> {
    try {
      const result = await apiRequest(`/pedidos/${pedidoId}/confirmar`, {
        method: 'PATCH',
        body: JSON.stringify({
          numero_pedido: numeroPedido,
          arquivo_id: arquivoId,
          usuario_id: usuarioId
        })
      });
      
      return { data: result.data, error: null };
    } catch (error) {
      console.error('Erro ao confirmar pedido:', error);
      return { data: null, error };
    }
  }

  // Adicionar documento digitado ao pedido
  async adicionarDocumentoDigitado(
    id: string,
    documento: DocumentoDigitado,
    digitadoPor?: string
  ): Promise<{ data: Pedido | null; error: any }> {
    try {
      const result = await apiRequest(`/pedidos/${id}/documento`, {
        method: 'PATCH',
        body: JSON.stringify({ documento, digitado_por: digitadoPor })
      });
      return { data: result.data, error: null };
    } catch (error) {
      console.error('Erro ao adicionar documento digitado:', error);
      return { data: null, error };
    }
  }

  // Finalizar pedido (marcar como completamente digitado)
  async finalizarPedido(id: string, digitadoPor?: string): Promise<{ data: Pedido | null; error: any }> {
    try {
      const result = await apiRequest(`/pedidos/${id}/finalizar`, {
        method: 'PATCH',
        body: JSON.stringify({ digitado_por: digitadoPor })
      });
      return { data: result.data, error: null };
    } catch (error) {
      console.error('Erro ao finalizar pedido:', error);
      return { data: null, error };
    }
  }

  // Excluir pedido
  async excluirPedido(id: string): Promise<{ error: any }> {
    try {
      await apiRequest(`/pedidos/${id}`, { method: 'DELETE' });
      return { error: null };
    } catch (error) {
      console.error('Erro ao excluir pedido:', error);
      return { error };
    }
  }

  // Calcular prioridade baseado na data desejada
  calcularPrioridade(dataDesejada?: string): number {
    if (!dataDesejada) return 0;

    const hoje = new Date();
    const data = new Date(dataDesejada);
    const diffDias = Math.ceil((data.getTime() - hoje.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDias < 0) return 5;
    if (diffDias === 0) return 4;
    if (diffDias === 1) return 4;
    if (diffDias <= 3) return 3;
    if (diffDias <= 6) return 2;
    return 1;
  }

  // Obter contagem de pedidos por status
  async getContagemPorStatus(filial?: string): Promise<{ [key: string]: number }> {
    try {
      const params = filial ? `?filial=${filial}` : '';
      const result = await apiRequest(`/pedidos/contagem${params}`);
      return result.data || {};
    } catch (error) {
      console.error('Erro ao obter contagem por status:', error);
      return {};
    }
  }
}

export const pedidosService = PedidosService.getInstance();
